import { useState } from "react";
import { api } from "../api";
import { ErrorNote, Loading, SectionTitle } from "../components/Feedback";
import MemberPicker, { personLabel } from "../components/MemberPicker";
import { Badge, Btn, Card, Field, Select } from "../components/Shared";
import { useApi } from "../useApi";
import { SR_ONLY, SuccessNote, formatWhen } from "./admin/parts";

// The Members card of the project page (N4a/N4c, 2026-10-13: a project has members, each a
// curator or a user). Every member reads the list (GET /projects/{id}/members, curators first); whoever may
// manage the project (`can_manage`: a curator of it, or an admin) adds a member from the active
// accounts (GET /users, MemberPicker), changes a project role (curator | user) and removes a
// member. Each change sends the whole new list: PUT /projects/{id}/members {members: [{user_id,
// role}]}. The server keeps at least one curator (409, its sentence shown in role="alert") and
// refuses an unknown or deactivated account (422). On a hosted project a newcomer reads; a
// removed member loses their data role, SQL login and API tokens (the server does that).

export const PROJECT_ROLES = [
  { value: "curator", label: "curator" },
  { value: "user", label: "user" },
];

const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#4b5563",
             textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "8px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle" };

export default function ProjectMembers({ project, onChanged }) {
  const listed = useApi(`/projects/${project.id}/members`);
  const accounts = useApi(project.can_manage ? "/users" : null);
  const [saved, setSaved] = useState(null);            // the list the last PUT answered
  const [picked, setPicked] = useState(null);
  const [newRole, setNewRole] = useState("user");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const rows = saved || listed.data;
  // A project the caller cannot see answers 404 and the page says so already.
  if (listed.error && listed.error.status === 404) return null;

  const save = async (next, message) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const out = await api.put(`/projects/${project.id}/members`,
                                { members: next.map(m => ({ user_id: m.user_id, role: m.role })) });
      setSaved(out);
      setNotice(message);
      if (onChanged) onChanged();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setRole = (m, role) => save(rows.map(r => (r.user_id === m.user_id ? { ...r, role } : r)),
                                    `${m.username} is now a ${role} of this project.`);
  const remove = m => save(rows.filter(r => r.user_id !== m.user_id), `${m.username} removed from this project.`);
  const add = async e => {
    e.preventDefault();
    if (!picked) return;
    const ok = await save([...rows, { user_id: picked.id, role: newRole }],
                          `${picked.username} added as a ${newRole}.`);
    if (ok) { setPicked(null); setNewRole("user"); }
  };

  const manage = project.can_manage;
  const curators = rows ? rows.filter(m => m.role === "curator").length : 0;

  return (
    <section aria-label="Members" style={{ marginBottom: 24 }}>
      <SectionTitle>Members</SectionTitle>
      <Card style={{ padding: "18px 22px" }}>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#4b5563" }}>
          Curators manage the project, its members and its database design, and edit every study.
          Users add studies and edit the ones they wrote.
        </p>
        <ErrorNote error={listed.error || error} />
        <SuccessNote>{notice}</SuccessNote>
        {!rows && listed.loading && <Loading />}
        {rows && (
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <caption style={{ textAlign: "left", fontSize: 12, color: "#4b5563", paddingBottom: 6 }}>
                {rows.length} member{rows.length === 1 ? "" : "s"} of {project.name}, {curators} curator{curators === 1 ? "" : "s"}
              </caption>
              <thead>
                <tr>
                  <th scope="col" style={th}>Member</th>
                  <th scope="col" style={th}>Project role</th>
                  <th scope="col" style={th}>Added</th>
                  {manage && <th scope="col" style={th}><span style={SR_ONLY}>Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(m => (
                  <tr key={m.user_id} data-member={m.username}>
                    <th scope="row" style={{ ...td, textAlign: "left", fontWeight: 600 }}>
                      {personLabel(m)}
                      {m.is_active === false && <> <Badge color="red">deactivated</Badge></>}
                    </th>
                    <td data-label="Project role" style={td}>
                      {manage ? (
                        <Select aria-label={`Project role of ${m.username}`} options={PROJECT_ROLES} value={m.role}
                                disabled={busy} onChange={e => e.target.value && e.target.value !== m.role && setRole(m, e.target.value)} />
                      ) : (
                        <Badge color={m.role === "curator" ? "green" : "blue"}>{m.role}</Badge>
                      )}
                    </td>
                    <td data-label="Added" style={td}>{formatWhen(m.added_at) || "—"}</td>
                    {manage && (
                      <td style={td}>
                        <Btn variant="secondary" disabled={busy} onClick={() => remove(m)}
                             style={{ padding: "5px 12px", fontSize: 13 }}>
                          Remove<span style={SR_ONLY}> {m.username}</span>
                        </Btn>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {manage && rows && (
          <form onSubmit={add} aria-label="Add a member"
                style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <MemberPicker id={`member-picker-${project.id}`} accounts={accounts.data} disabled={busy}
                          exclude={rows.map(m => m.user_id)} onPick={a => { setPicked(a); setNotice(null); }} />
            <Field label="Role of the new member">
              <Select options={PROJECT_ROLES} value={newRole} onChange={e => e.target.value && setNewRole(e.target.value)} />
            </Field>
            <Btn type="submit" disabled={busy || !picked}>
              {picked ? `Add ${picked.username}` : "Add member"}
            </Btn>
          </form>
        )}
        {manage && accounts.error && <ErrorNote error={accounts.error} />}
      </Card>
    </section>
  );
}
